import { LogOut, User, ChevronDown } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/lib/apiClient';
import { TrainerBatchSwitcher } from '@/components/TrainerBatchSwitcher';
import { CommandPalette } from '@/components/CommandPalette';
import { NotificationBell } from '@/components/NotificationBell';
import { MessageIconButton } from '@/components/MessageIconButton';
import { Avatar } from '@/features/Profile/Avatar';
import { useTrainerQuestionInbox } from '@/lib/hooks/useTopicMessages';

interface TrainerTopBarProps {
  unreadCount?: number;
}

export function TrainerTopBar({ unreadCount = 0 }: TrainerTopBarProps) {
  const { user, clearAuth } = useAuthStore();
  const username = user?.username ?? 'Trainer';
  const navigate = useNavigate();
  const { data: threads = [] } = useTrainerQuestionInbox(true);
  const needsReply = threads.filter((t) => t.last_sender_id === t.student_id).length;

  const handleLogout = async () => {
    try { await apiClient.post('/api/v1/auth/logout'); } catch { /* ignore */ }
    clearAuth();
    navigate({ to: '/' });
  };

  return (
    <header className="flex h-[90px] flex-shrink-0 items-center gap-3 border-b border-[#f1f1f1] bg-white px-4 lg:px-8">
      {/* Batch switcher */}
      <TrainerBatchSwitcher />

      {/* Search — compact icon on mobile, centered pill on desktop */}
      <div className="flex flex-1 justify-end md:justify-center">
        <div className="w-auto md:w-full md:max-w-lg">
          <CommandPalette />
        </div>
      </div>

      {/* Right: bell + user profile */}
      <div className="flex shrink-0 items-center gap-3">
        <MessageIconButton count={needsReply} to="/trainer/questions" className="lg:hidden" />

        <NotificationBell unreadCount={unreadCount} />

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="flex items-center gap-2.5 rounded-xl px-2.5 py-1.5 outline-none transition-colors hover:bg-[#fafafa]"
            >
              <Avatar src={user?.avatarUrl} name={username} size={32} />
              <div className="hidden flex-col items-start sm:flex">
                <span className="text-[13px] font-semibold leading-tight text-[#111827]">{username}</span>
                <span className="text-[11px] capitalize leading-tight text-[#6b7280]">
                  {user?.role ?? 'Trainer'}
                </span>
              </div>
              <ChevronDown className="hidden h-3.5 w-3.5 text-[#6b7280] sm:block" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-50 w-[260px] rounded-xl border border-[#ececec] bg-white shadow-xl"
              sideOffset={8}
              align="end"
            >
              <div className="flex items-center gap-3 p-4 pb-3">
                <Avatar src={user?.avatarUrl} name={username} size={40} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#111827] truncate">{username}</p>
                  <p className="text-xs text-[#6b7280] truncate">{user?.email}</p>
                </div>
              </div>

              <DropdownMenu.Separator className="h-px bg-border" />

              <div className="p-1.5">
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-[#fafafa] outline-none"
                  onSelect={() => navigate({ to: '/trainer/settings' })}
                >
                  <User className="h-4 w-4 text-[#6b7280]" />
                  <span>Settings</span>
                </DropdownMenu.Item>
              </div>

              <DropdownMenu.Separator className="h-px bg-border" />

              <div className="p-1.5">
                <DropdownMenu.Item
                  onSelect={() => handleLogout()}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-destructive hover:bg-destructive/10 outline-none"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenu.Item>
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
